import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useSelector } from 'react-redux';

import arrow_down from 'assets/img/icons/24px/arrow_down_lang.svg';
import arrow_up from 'assets/img/icons/24px/arrow_up_lang.svg';
import emirates from 'assets/img/icons/24px/emirates.svg';
import english from 'assets/img/icons/24px/english.svg';
import french from 'assets/img/icons/24px/french.svg';
import indian from 'assets/img/icons/24px/india.svg';
import portugal from 'assets/img/icons/24px/portugal.svg';
import spain from 'assets/img/icons/24px/spanish.svg';
import thailand from 'assets/img/icons/24px/thailand.svg';
import classNames from 'classnames';
import { useIsRtl } from 'hooks/isRtl';
import type { SupportedLanguage } from 'i18n';

import LocalizedLink from 'components/localized-link';
import { Logo } from 'components/logo';

import { isUSAUserSelector } from 'data/selectors/user';

import { LOCAL_STORAGE_KEYS } from 'ts/constants/local-storage-keys';
import { ELanguage } from 'ts/enums/language';

import facebook from './assets/facebook.svg';
import linkedIn from './assets/linkedIn.svg';
import pinterest from './assets/pinterest.svg';
import twitter from './assets/twitter.svg';
import type { IFooterInteractor } from './interactor';

const lngs: Record<ELanguage, { nativeName: string; flag: string }> = {
  [ELanguage.ENGLISH]: { nativeName: 'English', flag: english },
  [ELanguage.HINDI]: { nativeName: 'Hindi', flag: indian },
  [ELanguage.PORTUGUESE]: { nativeName: 'Português', flag: portugal },
  [ELanguage.SPANISH]: { nativeName: 'Español', flag: spain },
  [ELanguage.THAI]: { nativeName: 'ไทย', flag: thailand },
  [ELanguage.ARABIC]: { nativeName: 'العربية', flag: emirates },
  [ELanguage.FRENCH]: { nativeName: 'Français', flag: french }
};

export interface IProps {
  interactor: IFooterInteractor;
  backgroundColor?: string;
}

export const FooterRouter: React.FC<IProps> = ({ interactor, backgroundColor }) => {
  const { t } = useTranslation();
  const chosenLanguageRef = useRef(interactor.userLanguage);
  const isRtl = useIsRtl();
  const isUSAUser = useSelector(isUSAUserSelector);
  const displayNotice = useMemo(() => {
    const isAutoTest = !!localStorage.getItem(LOCAL_STORAGE_KEYS.IS_AUTO_TEST);
    return isUSAUser && !isAutoTest;
  }, [isUSAUser]);

  const handleChangingLanguage = (lang: SupportedLanguage) => {
    interactor.clickOnChangeLang(lang);
    chosenLanguageRef.current = lang;
  };

  useEffect(() => {
    chosenLanguageRef.current = interactor.userLanguage;
  }, [interactor.userLanguage]);

  const DropdownLanguages = () => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const clickOutsideHandler = (event: any) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    useEffect(() => {
      document.addEventListener('mousedown', clickOutsideHandler);
      return () => document.removeEventListener('mousedown', clickOutsideHandler);
    }, []);
    return (
      <div
        ref={dropdownRef}
        className='relative'
      >
        <button
          type='button'
          onClick={() => setIsOpen(!isOpen)}
          className={classNames(
            'bg-[#1D1D1D] px-4 py-2 w-[164px] rounded-[10px] text-[#FFFFFF] text-[16px] flex items-center justify-between border-[1px] border-[#E6E6E6] mb-6 tablet:mb-0',
            { 'border-[0] border-[#F9F9F9]': isOpen }
          )}
        >
          <div className='flex items-center gap-x-[6px]'>
            <LazyLoadImage
              src={lngs[chosenLanguageRef.current]?.flag}
              width='24px'
              height='24px'
              alt='flag'
            />
            {lngs[chosenLanguageRef.current]?.nativeName}
          </div>
          {isOpen ? (
            <LazyLoadImage
              src={arrow_up}
              width='14px'
              height='8px'
              alt='arrow_up'
            />
          ) : (
            <LazyLoadImage
              src={arrow_down}
              width='14px'
              height='8px'
              alt='arrow_down'
            />
          )}
        </button>
        {isOpen && (
          <ul className='absolute top-full left-0 z-50'>
            {(Object.keys(lngs) as SupportedLanguage[]).map((lng) => (
              <li
                key={lng}
                onClick={() => handleChangingLanguage(lng)}
                className={classNames(
                  `bg-[#1D1D1D] hover:bg-[#2c2c2c] py-2 pl-4 cursor-pointer
                  w-[164px] text-[#FFFFFF] first:rounded-[10px_10px_0_0] text-[16px]
                  text-center border-[#9191912a] last:border-b-[1px] flex gap-x-[6px]`,
                  {
                    'pr-4': isRtl
                  }
                )}
              >
                <LazyLoadImage
                  src={lngs[lng].flag}
                  alt='flag'
                />
                {lngs[lng].nativeName}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <div style={{ backgroundColor: backgroundColor }}>
      <div className='bg-[#1D1D1D] relative text-[#CACACA] py-[48px] px-4'>
        <div className='w-full max-w-[1140px] mx-auto'>
          <div className='flex flex-col tablet:flex-row justify-between gap-6 tablet:gap-0'>
            <div className=''>
              <Logo />
            </div>
            <div>
              <div className='font-bold text-white mb-3'>{t('footer.tools')}</div>
              <div className='flex flex-col tablet:flex-row justify-between gap-6 tablet:gap-10'>
                <div>
                  <div
                    className='border-box mt-1 pb-2 hover:text-white border-b-[1px] border-solid border-transparent hover:border-[#A5A5A5]'
                    onClick={interactor.tools.pdf_to_word.onClick}
                    role='button'
                  >
                    <LocalizedLink
                      href={interactor.tools.pdf_to_word.href}
                      rel='noopener noreferrer'
                      target='_blank'
                    >
                      {interactor.tools.pdf_to_word.text}
                    </LocalizedLink>
                  </div>
                  <div
                    className='border-box pb-2 hover:text-white border-b-[1px] border-solid border-transparent hover:border-[#A5A5A5]'
                    onClick={interactor.tools.edit.onClick}
                    role='button'
                  >
                    <LocalizedLink
                      href={interactor.tools.edit.href}
                      rel='noopener noreferrer'
                      target='_blank'
                    >
                      {interactor.tools.edit.text}
                    </LocalizedLink>
                  </div>
                  <div
                    className='border-box mt-1 pb-2 hover:text-white border-b-[1px] border-solid border-transparent hover:border-[#A5A5A5]'
                    onClick={interactor.tools.compress.onClick}
                    role='button'
                  >
                    <LocalizedLink
                      href={interactor.tools.compress.href}
                      rel='noopener noreferrer'
                      target='_blank'
                    >
                      {interactor.tools.compress.text}
                    </LocalizedLink>
                  </div>
                  <div
                    className='border-box mt-1 pb-2 hover:text-white border-b-[1px] border-solid border-transparent hover:border-[#A5A5A5]'
                    onClick={interactor.tools.sign_pdf.onClick}
                    role='button'
                  >
                    <LocalizedLink
                      href={interactor.tools.sign_pdf.href}
                      rel='noopener noreferrer'
                      target='_blank'
                    >
                      {interactor.tools.sign_pdf.text}
                    </LocalizedLink>
                  </div>
                  <div
                    className='border-box mt-1 pb-2 hover:text-white border-b-[1px] border-solid border-transparent hover:border-[#A5A5A5]'
                    onClick={interactor.tools.merge_pdf.onClick}
                    role='button'
                  >
                    <LocalizedLink
                      href={interactor.tools.merge_pdf.href}
                      rel='noopener noreferrer'
                      target='_blank'
                    >
                      {interactor.tools.merge_pdf.text}
                    </LocalizedLink>
                  </div>
                  <div
                    className='border-box mt-1 pb-2 hover:text-white border-b-[1px] border-solid border-transparent hover:border-[#A5A5A5]'
                    onClick={interactor.tools.merge_pdf.onClick}
                    role='button'
                  >
                    <LocalizedLink
                      href={interactor.tools.pdf_converter.href}
                      rel='noopener noreferrer'
                      target='_blank'
                    >
                      {interactor.tools.pdf_converter.text}
                    </LocalizedLink>
                  </div>
                </div>
                <div>
                  <div
                    className='border-box mt-1 pb-2 hover:text-white border-b-[1px] border-solid border-transparent hover:border-[#A5A5A5]'
                    onClick={interactor.tools.pdf_to_excel.onClick}
                    role='button'
                  >
                    <LocalizedLink
                      href={interactor.tools.pdf_to_excel.href}
                      rel='noopener noreferrer'
                      target='_blank'
                    >
                      {interactor.tools.pdf_to_excel.text}
                    </LocalizedLink>
                  </div>
                  <div
                    className='border-box mt-1 pb-2 hover:text-white border-b-[1px] border-solid border-transparent hover:border-[#A5A5A5]'
                    onClick={interactor.tools.pdf_to_pptx.onClick}
                    role='button'
                  >
                    <LocalizedLink
                      href={interactor.tools.pdf_to_pptx.href}
                      rel='noopener noreferrer'
                      target='_blank'
                    >
                      {interactor.tools.pdf_to_pptx.text}
                    </LocalizedLink>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className='font-bold text-white mb-3'>{t('footer.company')}</div>

              <div
                className='border-box mt-1 pb-2 hover:text-white border-b-[1px] border-solid border-transparent hover:border-[#A5A5A5]'
                onClick={interactor.navigationLinks.contactUs.onClick}
                role='button'
              >
                <LocalizedLink
                  href={interactor.navigationLinks.contactUs.href}
                  rel='noopener noreferrer'
                  target='_blank'
                >
                  {interactor.navigationLinks.contactUs.text}
                </LocalizedLink>
              </div>
            </div>
            <div className='border-[0.5px] border-solid border-[#A5A5A5] tablet:hidden' />
            <div>
              {displayNotice && (
                <div
                  className='border-box mt-1 pb-2 hover:text-white border-b-[1px] border-solid border-transparent hover:border-[#A5A5A5]'
                  onClick={interactor.notice.onClick}
                  role='button'
                >
                  <LocalizedLink
                    href={interactor.notice.href}
                    rel='noopener noreferrer'
                    target='_blank'
                  >
                    {interactor.notice.text}
                  </LocalizedLink>
                </div>
              )}
            </div>
            <div className='border-[0.5px] border-solid border-[#A5A5A5] tablet:hidden' />
            <div>
              <div className='font-bold text-white mb-3'>{t('footer.follow_us')}</div>
              <div
                className='flex gap-2 border-box pb-2 hover:text-white border-b-[1px] border-solid border-transparent hover:border-[#A5A5A5]'
                onClick={interactor.linkedIn.onClick}
                role='button'
              >
                <LazyLoadImage
                  src={linkedIn}
                  width='24px'
                  height='27px'
                  alt='linkedIn'
                />
                <LocalizedLink
                  href={interactor.linkedIn.href}
                  rel='noopener noreferrer'
                  target='_blank'
                >
                  {interactor.linkedIn.text}
                </LocalizedLink>
              </div>
              <div
                className='flex gap-2 border-box mt-1 pb-2 hover:text-white border-b-[1px] border-solid border-transparent hover:border-[#A5A5A5]'
                onClick={interactor.facebook.onClick}
                role='button'
              >
                <LazyLoadImage
                  src={facebook}
                  width='24px'
                  height='27px'
                  alt='linkedIn'
                />
                <LocalizedLink
                  href={interactor.facebook.href}
                  rel='noopener noreferrer'
                  target='_blank'
                >
                  {interactor.facebook.text}
                </LocalizedLink>
              </div>
              <div
                className='flex gap-2 border-box mt-1 pb-2 hover:text-white border-b-[1px] border-solid border-transparent hover:border-[#A5A5A5]'
                onClick={interactor.twitter.onClick}
                role='button'
              >
                <LazyLoadImage
                  src={twitter}
                  width='24px'
                  height='27px'
                  alt='linkedIn'
                />
                <LocalizedLink
                  href={interactor.twitter.href}
                  rel='noopener noreferrer'
                  target='_blank'
                >
                  {interactor.twitter.text}
                </LocalizedLink>
              </div>
              <div
                className='flex gap-2 border-box mt-1 pb-2 hover:text-white border-b-[1px] border-solid border-transparent hover:border-[#A5A5A5]'
                onClick={interactor.pinterest.onClick}
                role='button'
              >
                <LazyLoadImage
                  src={pinterest}
                  width='24px'
                  height='27px'
                  alt='linkedIn'
                />
                <LocalizedLink
                  href={interactor.pinterest.href}
                  rel='noopener noreferrer'
                  target='_blank'
                >
                  {interactor.pinterest.text}
                </LocalizedLink>
              </div>
            </div>
            <div className='border-[0.5px] border-solid border-[#A5A5A5] tablet:hidden' />
            <div>
              <div className='font-bold text-white mb-3'>{t('footer.language')}</div>
              <DropdownLanguages />
            </div>
          </div>
          <div className='text-center mt-3 tablet:mt-8'>
            <span className='max-w-[390px]'>{interactor.copyright}</span>{' '}
            <span>
              {interactor.isUSA ? 'GURUDOCS LIMITED, 9205 West Russell Road, Las Vegas, Nevada, 89148, USA' : null}
              {!interactor.isUSA && interactor.isUAE
                ? 'FT SICH FZCO, IFZA Business Park, DDP, Building A2, 21379-001, Dubai, United Arab'
                : null}
              {!interactor.isUSA && !interactor.isUAE
                ? 'LOPOFIST LIMITED, Stavrou 26, Strovolos, 1065, Nicosia, Cyprus.'
                : null}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
